import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Store, select } from '@ngrx/store';
import { from } from 'rxjs';
import { concatMap, filter, map, mergeMap, switchMap } from 'rxjs/operators';

import { MAIN_NAVIGATION_MAX_SUB_CATEGORIES_DEPTH } from 'ish-core/configurations/injection-keys';
import { CategoryHelper } from 'ish-core/models/category/category.model';
import { ofCategoryUrl } from 'ish-core/routing/category/category.route';
import { CategoriesService } from 'ish-core/services/categories/categories.service';
import { selectRouteParam } from 'ish-core/store/core/router';
import { setBreadcrumbData } from 'ish-core/store/core/viewconf';
import { personalizationStatusDetermined } from 'ish-core/store/customer/user';
import { loadMoreProducts } from 'ish-core/store/shopping/product-listing';
import { HttpStatusCodeService } from 'ish-core/utils/http-status-code/http-status-code.service';
import { InjectSingle } from 'ish-core/utils/injection';
import {
  delayUntil,
  mapErrorToAction,
  mapToPayload,
  mapToPayloadProperty,
  useCombinedObservableOnAction,
  whenTruthy,
} from 'ish-core/utils/operators';

import {
  fetchSpecificCategory,
  loadCategory,
  loadCategoryByRef,
  loadCategoryFail,
  loadCategoryLinks,
  loadCategoryLinksFail,
  loadCategoryLinksSuccess,
  loadCategorySuccess,
  loadCategoryTree,
  loadCategoryTreeSuccess,
  loadSingleCategoryByRef,
  loadSingleCategoryByRefFail,
  loadSingleCategoryByRefSuccess,
  loadSingleCategoryByUniqueId,
  loadSingleCategoryByUniqueIdFail,
  loadTopLevelCategories,
  loadTopLevelCategoriesFail,
  loadTopLevelCategoriesSuccess,
} from './categories.actions';
import {
  getBreadcrumbForCategoryPage,
  getCategoryEntities,
  getCategoryRefs,
  getSelectedCategory,
} from './categories.selectors';

@Injectable()
export class CategoriesEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private categoryService: CategoriesService,
    @Inject(MAIN_NAVIGATION_MAX_SUB_CATEGORIES_DEPTH)
    private mainNavigationMaxSubCategoriesDepth: InjectSingle<typeof MAIN_NAVIGATION_MAX_SUB_CATEGORIES_DEPTH>,
    private httpStatusCodeService: HttpStatusCodeService
  ) {}

  /**
   * listens to routing and fires {@link LoadCategory}
   * when the requested {@link Category} is not available, yet
   */
  selectedCategory$ = createEffect(() =>
    this.actions$.pipe(
      useCombinedObservableOnAction(
        this.store.pipe(select(selectRouteParam('categoryUniqueId'))),
        personalizationStatusDetermined
      ),
      whenTruthy(),
      concatLatestFrom(() => this.store.pipe(select(getCategoryEntities))),
      filter(([id, entities]) => !CategoryHelper.isCategoryCompletelyLoaded(entities[id])),
      map(([categoryId]) => loadCategory({ categoryId }))
    )
  );

  /**
   * listens to routing and fires {@link loadCategoryByRef}
   * when the requested ref in {@link getCategoryRefs} is not available, yet
   */
  selectedCategoryRef$ = createEffect(() =>
    this.actions$.pipe(
      useCombinedObservableOnAction(
        this.store.pipe(select(selectRouteParam('categoryRefId'))),
        personalizationStatusDetermined
      ),
      whenTruthy(),
      concatLatestFrom(() => [this.store.pipe(select(getCategoryRefs)), this.store.pipe(select(getCategoryEntities))]),
      filter(
        ([id, refs, entities]) =>
          !refs[id] || (refs[id] && !CategoryHelper.isCategoryCompletelyLoaded(entities[refs[id]]))
      ),
      map(([categoryId]) => loadCategoryByRef({ categoryId }))
    )
  );

  /**
   * loads a {@link Category} using the {@link CategoriesService}
   */
  loadCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCategory, loadCategoryByRef),
      mapToPayloadProperty('categoryId'),

      mergeMap(id =>
        this.categoryService.getCategory(id).pipe(
          map(categories => loadCategorySuccess({ categories })),
          mapErrorToAction(loadCategoryFail)
        )
      )
    )
  );

  fetchSpecificCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchSpecificCategory),
      mapToPayloadProperty('categoryId'),
      concatLatestFrom(() => this.store.pipe(select(getCategoryEntities))),
      filter(([id, entities]) => !CategoryHelper.isCategoryCompletelyLoaded(entities[id])),
      map(([categoryId]) => loadCategory({ categoryId }))
    )
  );

  loadTopLevelCategories$ = createEffect(() =>
    this.actions$.pipe(
      useCombinedObservableOnAction(
        this.actions$.pipe(ofType(loadTopLevelCategories)),
        personalizationStatusDetermined
      ),
      switchMap(() =>
        this.categoryService.getTopLevelCategories(SSR ? 0 : this.mainNavigationMaxSubCategoriesDepth).pipe(
          map(categories => loadTopLevelCategoriesSuccess({ categories })),
          mapErrorToAction(loadTopLevelCategoriesFail)
        )
      )
    )
  );

  loadCategoryTree$ = createEffect(() =>
    this.actions$.pipe(
      useCombinedObservableOnAction(this.actions$.pipe(ofType(loadCategoryTree)), personalizationStatusDetermined),
      mapToPayload(),
      switchMap(({ categoryRef, depth }) =>
        this.categoryService
          .getCategoryTree(categoryRef, depth)
          .pipe(map(categories => loadCategoryTreeSuccess({ categories })))
      )
    )
  );

  productOrCategoryChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigatedAction),
      switchMap(() =>
        this.store.pipe(
          ofCategoryUrl(),
          select(getSelectedCategory),
          whenTruthy(),
          filter(cat => cat.hasOnlineProducts),
          map(({ uniqueId }) => loadMoreProducts({ id: { type: 'category', value: uniqueId } }))
        )
      )
    )
  );

  redirectIfErrorInCategories$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadCategoryFail),
        concatMap(() => from(this.httpStatusCodeService.setStatus(404)))
      ),
    { dispatch: false }
  );

  setBreadcrumbForCategoryPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigatedAction),
      switchMap(() =>
        this.store.pipe(
          ofCategoryUrl(),
          select(getBreadcrumbForCategoryPage),
          whenTruthy(),
          map(breadcrumbData => setBreadcrumbData({ breadcrumbData }))
        )
      )
    )
  );

  /* DIAS Customization Start */
  /**
   * loads a {@link Category} using the {@link CategoriesService} loadSingleCategoryByRefSuccess
   */
  loadSingleCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSingleCategoryByRef),
      mapToPayloadProperty('categoryId'),
      mergeMap(id =>
        this.categoryService.getCategoryByRefId(id).pipe(
          map(categoryUniqueId => loadSingleCategoryByRefSuccess({ categoryUniqueId })),
          mapErrorToAction(loadSingleCategoryByRefFail)
        )
      )
    )
  );
  loadSingleCategoryByRefSuccess$ = createEffect(() =>
    this.actions$.pipe(
      useCombinedObservableOnAction(
        this.actions$.pipe(ofType(loadSingleCategoryByRefSuccess)),
        personalizationStatusDetermined
      ),
      mapToPayloadProperty('categoryUniqueId'),
      mergeMap(id =>
        this.categoryService.getCategoryByUniqueId(id).pipe(
          map(categories => loadCategorySuccess({ categories })),
          mapErrorToAction(loadSingleCategoryByRefFail)
        )
      )
    )
  );
  loadSingleCategoryById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSingleCategoryByUniqueId),
      delayUntil(this.actions$.pipe(ofType(personalizationStatusDetermined))),
      mapToPayloadProperty('uniqueId'),
      mergeMap(id =>
        this.categoryService.getCategory(id).pipe(
          map(categories => loadCategorySuccess({ categories })),
          mapErrorToAction(loadSingleCategoryByUniqueIdFail)
        )
      )
    )
  );
  /* DIAS Customization End */

  loadCategoryLinks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCategoryLinks),
      mapToPayloadProperty('categoryId'),
      mergeMap(categoryId =>
        this.categoryService.getCategoryProductLinks(categoryId).pipe(
          map(links => loadCategoryLinksSuccess({ categoryId, links })),
          mapErrorToAction(loadCategoryLinksFail, { categoryId })
        )
      )
    )
  );
}
